/* 코스 상세 (/travel/course/:courseId)
 *
 * "이 코스를 따라갈지" 를 판단하게 하는 화면이라 일정 전체를 그대로
 * 보여 줍니다. 요약만 보여 주면 판단이 안 됩니다.
 *
 * 머리에 "언제 다녀왔는지" 를 넣은 이유 — 같은 코스도 계절과 공사 여부에
 * 따라 전혀 다릅니다.
 */
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { AppScreen, Badge, Section, SectionHeader, StarRating, Button } from 'components/ds';
import { IcPin } from 'components/common/icons/EmojiIcons';
import { CourseCopySheet } from 'components/course/CourseCopySheet';
import { CourseRatingSection } from 'components/course/CourseRatingSection';
import { useTravelCourse, useCourseCopy, groupByDay } from 'hooks/useTravelCourse';
import styles from './TravelCourseDetail.module.css';

const fmtMonth = (iso?: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
};

export const TravelCourseDetail = () => {
    const { t } = useTranslation();
    const { courseId = '' } = useParams();
    const { showAlert } = useAlert();
    const { course, loading, reload } = useTravelCourse(courseId);
    const {
        targets, loadingTargets, targetsLoaded, loadTargets, copy, busy,
    } = useCourseCopy(courseId);
    const [sheetOpen, setSheetOpen] = useState(false);

    const days = useMemo(() => groupByDay(course?.places), [course]);

    /* 담기 결과를 사실대로 말합니다 — 몇 곳 담고 몇 곳은 이미 있었는지.
       "담았어요" 만 띄우면 7곳 중 2곳만 들어간 걸 알 수 없습니다. */
    const onPick = useCallback(async (meetingId: string) => {
        const res = await copy(meetingId);
        if (!res.ok) {
            showAlert({ message: res.message || t('course.copyFailed') });
            return;
        }
        setSheetOpen(false);
        const { addedCount, skippedCount, folded, targetDays } = res.result;
        const parts = [t('course.copiedN', { n: addedCount })];
        if (skippedCount > 0) parts.push(t('course.copySkipped', { n: skippedCount }));
        if (folded) parts.push(t('course.copyFolded', { n: targetDays }));
        await showAlert({ message: parts.join('\n') });
        /* 따라한 수와 별점 자격이 바뀌었으니 다시 읽습니다 */
        reload();
    }, [copy, showAlert, t, reload]);

    if (!course) {
        return (
            <AppScreen title={t('course.detailTitle')} bar="sub">
                <Section>
                    <p className={styles.empty}>
                        {loading ? t('common.loading') : t('course.notFound')}
                    </p>
                </Section>
            </AppScreen>
        );
    }

    return (
        <AppScreen title={t('course.detailTitle')} bar="sub" hasFooter>
            <Section spacing="tight">
                <h1 className={styles.title}>{course.title}</h1>

                {/* 누가 · 언제 · 별점 — 언제가 중요합니다 */}
                <div className={styles.meta}>
                    <span>{course.creatorNickname || ''}</span>
                    {course.createdAt && <span className={styles.dot}>·</span>}
                    {course.createdAt && <span>{fmtMonth(course.createdAt)}</span>}
                    {course.ratingAvg != null && (
                        <span className={styles.rating}>
                            <StarRating
                                value={Number(course.ratingAvg)}
                                count={course.ratingCount || 0}
                                size={13}
                                source="CREW"
                            />
                        </span>
                    )}
                </div>

                {course.summary && <p className={styles.summary}>{course.summary}</p>}

                {/* 규모를 한눈에 — 따라한 수는 표본 크기입니다 */}
                <div className={styles.badges}>
                    <Badge tone="outline">
                        {course.days && course.days > 1
                            ? t('course.nights', { n: course.days - 1, d: course.days })
                            : t('course.oneDay')}
                    </Badge>
                    <Badge tone="outline">{t('course.placeCount', { n: course.placeCount || 0 })}</Badge>
                    {(course.copiedCount || 0) > 0 && (
                        <Badge tone="brand">{t('course.copiedCount', { n: course.copiedCount })}</Badge>
                    )}
                </div>
            </Section>

            {days.map(({ day, items }) => (
                <Section key={String(day)} divided>
                    <SectionHeader
                        title={day ? `Day ${day}` : t('course.noDay')}
                        sub={t('course.placeCount', { n: items.length })}
                    />
                    <ul className={styles.list}>
                        {items.map((p) => (
                            <li key={p.coursePlaceId} className={styles.row}>
                                {/* 시각은 왼쪽 고정폭 — 세로로 읽힙니다 */}
                                <b className={styles.time}>{p.visitTime || ''}</b>
                                <span className={styles.body}>
                                    <span className={styles.placeName}>{p.placeName}</span>
                                    {p.placeAddress && (
                                        <span className={styles.address}>
                                            <IcPin size={11} />{p.placeAddress}
                                        </span>
                                    )}
                                    {p.description && (
                                        <span className={styles.desc}>{p.description}</span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </Section>
            ))}

            {/* 별점은 따라해 본 사람만 — 자격이 없으면 영역을 아예 숨깁니다.
               조회수 기반 인기가 어떻게 무너지는지 TourAPI 에서 봤습니다. */}
            {course.copiedByMe && (
                <CourseRatingSection
                    courseId={courseId}
                    myReview={course.myReview}
                    onSaved={reload}
                />
            )}

            <Section divided last>
                <p className={styles.note}>{t('course.copyNote')}</p>
            </Section>

            <div className={styles.footer}>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    disabled={busy}
                    onClick={() => setSheetOpen(true)}
                >
                    {t('course.copyToMine')}
                </Button>
            </div>

            <CourseCopySheet
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                courseDays={course.days}
                targets={targets}
                loading={loadingTargets}
                loaded={targetsLoaded}
                busy={busy}
                onLoad={loadTargets}
                onPick={onPick}
            />
        </AppScreen>
    );
};
